import { useState, useEffect, useRef } from "react";
import { AIRPORTS, REGIONAL_AIRSPACE, CACHE_DURATION } from "./constants";
import { fetchFlightsFromAPI, getAirportByHubCode } from "./utils";
import { saveToCache, loadFromCache } from "./cacheUtils";
import { mergeAirlinesFromFlights } from "./airlineRegistry";

/**
 * Custom hook for fetching and caching flight data.
 * Handles in-memory cache, localStorage cache, periodic refresh,
 * heartbeat for mobile browsers, and tab visibility resume.
 */
function useFlights(selectedHub, onFlightsUpdate) {
  const [flightsData, setFlightsData] = useState({
    departures: [],
    arrivals: [],
  });
  const [flightsLoading, setFlightsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const cacheRef = useRef({});

  useEffect(() => {
    const fetchFlights = async () => {
      const now = Date.now();

      try {
        let airportCodes = [];
        if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
          airportCodes = AIRPORTS.map((airport) => airport.iataCode);
        } else {
          const airport = getAirportByHubCode(selectedHub);
          if (airport && airport.iataCode) {
            airportCodes = [airport.iataCode];
          }
        }

        if (airportCodes.length === 0) {
          setFlightsLoading(false);
          return;
        }

        const cacheKey = airportCodes.sort().join(",");

        // Check in-memory cache first — no loading flash needed
        const cached = cacheRef.current[cacheKey];
        if (cached && now - cached.timestamp < CACHE_DURATION.FLIGHTS) {
          setFlightsData(cached.data);
          setLastUpdated(cached.cacheUpdatedAt || new Date(cached.timestamp));
          if (onFlightsUpdate) {
            onFlightsUpdate(cached.data, selectedHub);
          }
          setFlightsLoading(false);
          return;
        }

        // Check localStorage cache — show stale data immediately while fetching fresh
        const stored = loadFromCache(
          `flights_${cacheKey}`,
          CACHE_DURATION.FLIGHTS,
        );
        if (stored) {
          setFlightsData(stored.data.flightsResult);
          setLastUpdated(
            stored.data.cacheUpdatedAt
              ? new Date(stored.data.cacheUpdatedAt)
              : new Date(stored.timestamp),
          );
          if (onFlightsUpdate) {
            onFlightsUpdate(stored.data.flightsResult, selectedHub);
          }
          setFlightsLoading(false);
        } else {
          setFlightsLoading(true);
        }

        // Fetch hubs sequentially to avoid Lambda cold start 500s
        const results = [];
        for (const code of airportCodes) {
          const depResult = await fetchFlightsFromAPI(code, "departure").catch(
            (err) => {
              console.error(`Failed to fetch departures for ${code}:`, err);
              return { flights: [], airport: code, cache_updated_at: null };
            },
          );
          results.push(depResult);

          const arrResult = await fetchFlightsFromAPI(code, "arrival").catch(
            (err) => {
              console.error(`Failed to fetch arrivals for ${code}:`, err);
              return { flights: [], airport: code, cache_updated_at: null };
            },
          );
          results.push(arrResult);
        }

        const allDepartures = [];
        const allArrivals = [];
        let mostRecentCacheUpdate = null;

        results.forEach((result, index) => {
          if (index % 2 === 0) {
            allDepartures.push(...(result.flights || []));
          } else {
            allArrivals.push(...(result.flights || []));
          }
          const cacheTimestamp = result.cache_updated_at || result.timestamp;
          if (cacheTimestamp) {
            const cacheTime = new Date(cacheTimestamp);
            if (!mostRecentCacheUpdate || cacheTime > mostRecentCacheUpdate) {
              mostRecentCacheUpdate = cacheTime;
            }
          }
        });

        const flightsResult = {
          departures: allDepartures,
          arrivals: allArrivals,
        };

        // Populate airline registry from API data
        mergeAirlinesFromFlights([...allDepartures, ...allArrivals]);

        setFlightsData(flightsResult);
        setLastUpdated(mostRecentCacheUpdate || new Date(now));
        if (onFlightsUpdate) {
          onFlightsUpdate(flightsResult, selectedHub);
        }
        cacheRef.current[cacheKey] = {
          data: flightsResult,
          timestamp: now,
          cacheUpdatedAt: mostRecentCacheUpdate,
        };
        saveToCache(`flights_${cacheKey}`, {
          flightsResult,
          cacheUpdatedAt: mostRecentCacheUpdate
            ? mostRecentCacheUpdate.toISOString()
            : null,
        });
      } catch (error) {
        console.error("Error fetching flights:", error);
      } finally {
        setFlightsLoading(false);
      }
    };

    fetchFlights();
    const interval = setInterval(fetchFlights, CACHE_DURATION.FLIGHTS);

    const heartbeat = setInterval(() => {
      const airportCodes =
        selectedHub === REGIONAL_AIRSPACE.hubCode
          ? AIRPORTS.map((a) => a.iataCode)
          : (() => {
              const ap = getAirportByHubCode(selectedHub);
              return ap?.iataCode ? [ap.iataCode] : [];
            })();
      const cacheKey = airportCodes.sort().join(",");
      const cached = cacheRef.current[cacheKey];
      if (!cached || Date.now() - cached.timestamp >= CACHE_DURATION.FLIGHTS) {
        fetchFlights();
      }
    }, 60 * 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        cacheRef.current = {};
        setIsRefreshing(true);
        fetchFlights().finally(() => {
          setTimeout(() => setIsRefreshing(false), 1500);
        });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(interval);
      clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [selectedHub, onFlightsUpdate]);

  return { flightsData, flightsLoading, lastUpdated, isRefreshing };
}

export default useFlights;
